import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/src/components/ui/chart";

import {
  CHART_CONTAINER_CLASS,
  CHART_MARGIN,
  getValueYAxisProps,
  X_AXIS_PROPS,
} from "./chartLayout";
import {
  formatClockTime,
  formatDayMonth,
  getValueAxisConfig,
} from "./charts.utils";

import type { ComponentProps, JSX } from "react";

import type { TimePoint } from "./charts.types";

type LabelFormatter = NonNullable<
  ComponentProps<typeof ChartTooltipContent>["labelFormatter"]
>;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface LiveViewersChartProps {
  pointList: readonly TimePoint[];
  domain: readonly [number, number];
  endTimestamp: number | null;
  label: string;
}

function LiveViewersChart({
  pointList,
  domain,
  endTimestamp,
  label,
}: LiveViewersChartProps): JSX.Element {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const config = useMemo(
    () => ({ viewers: { label, color: "var(--chart-1)" } }),
    [label],
  );
  const spansSeveralDays = domain[1] - domain[0] > MS_PER_DAY;
  const valueAxis = useMemo(
    () =>
      getValueAxisConfig(
        pointList.reduce((peak, point) => Math.max(peak, point.viewers), 0),
        lang,
      ),
    [pointList, lang],
  );

  const formatTick = (timestamp: number): string =>
    formatClockTime(timestamp, lang);
  const formatTooltipLabel: LabelFormatter = (_, payloadList) => {
    const timestamp = Number(payloadList[0]?.payload?.timestamp ?? 0);
    const clock = formatClockTime(timestamp, lang, true);
    return spansSeveralDays
      ? `${formatDayMonth(timestamp, lang)}, ${clock}`
      : clock;
  };

  return (
    <ChartContainer className={CHART_CONTAINER_CLASS} config={config}>
      <LineChart accessibilityLayer data={[...pointList]} margin={CHART_MARGIN}>
        <CartesianGrid vertical={false} />
        <XAxis
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...X_AXIS_PROPS}
          dataKey="timestamp"
          domain={[...domain]}
          minTickGap={32}
          scale="time"
          tickFormatter={formatTick}
          type="number"
        />
        <YAxis
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getValueYAxisProps(valueAxis)}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={formatTooltipLabel}
              valueFormatter={valueAxis.formatValue}
            />
          }
        />
        {endTimestamp === null ? null : (
          <ReferenceLine
            stroke="var(--muted-foreground)"
            strokeDasharray="4 4"
            x={endTimestamp}
          />
        )}
        <Line
          dataKey="viewers"
          dot={pointList.length === 1}
          stroke="var(--color-viewers)"
          strokeWidth={2}
          type="stepAfter"
        />
      </LineChart>
    </ChartContainer>
  );
}

export default LiveViewersChart;
